import React from 'react';
import { Book } from '@/types/book';
import { updateBookCacheVersion, shouldRefreshBookImages } from '@/lib/imageCache';

/**
 * Hook to automatically invalidate image cache when books are updated based on cache_version
 */
export function useBookCacheInvalidation(books: Book[]) {
  React.useEffect(() => {
    if (!books || !Array.isArray(books)) return;

    let needsRefresh = false;

    // Check each book for cache version updates
    books.forEach(book => {
      if (book.id && book.cache_version !== undefined) {
        const wasCleared = updateBookCacheVersion(book.id, book.cache_version);
        if (wasCleared) {
          needsRefresh = true;
        }
      }
    });

    // If any cache was cleared, trigger a page refresh for any open readers
    if (needsRefresh) {
      // Dispatch a custom event that reader components can listen to
      window.dispatchEvent(new CustomEvent('book-images-updated', {
        detail: { 
          books: books.map(b => ({ id: b.id, cache_version: b.cache_version })),
          timestamp: Date.now()
        }
      }));
      
      // For iOS devices, also trigger an immediate cache refresh
      const isIOS = /iPhone|iPad|iPod|Mac/.test(navigator.userAgent) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      if (isIOS) {
        // Slight delay to ensure the event propagates first
        setTimeout(() => {
          // Force refresh of all book images on iOS
          const images = document.querySelectorAll('img') as NodeListOf<HTMLImageElement>;
          const timestamp = Date.now();
          
          images.forEach(img => {
            if (img.src && img.src.includes('book-media')) {
              const url = new URL(img.src);
              url.searchParams.set('ios_refresh', timestamp.toString());
              if (img.src !== url.toString()) {
                img.src = url.toString();
              }
            }
          });
        }, 250);
      }
    }
  }, [books]);
}

/**
 * Hook for individual book components to listen for cache invalidation
 */
export function useBookImageRefresh(bookId: number, onRefresh?: () => void) {
  React.useEffect(() => {
    const handleImageUpdate = (event: CustomEvent) => {
      const updatedBooks = event.detail?.books || [];
      const thisBookUpdate = updatedBooks.find((b: any) => b.id === bookId);
      
      if (thisBookUpdate && onRefresh) {
        console.log(`Book ${bookId} cache version updated, triggering refresh`);
        onRefresh();
      }
    };

    window.addEventListener('book-images-updated', handleImageUpdate as EventListener);
    
    return () => {
      window.removeEventListener('book-images-updated', handleImageUpdate as EventListener);
    };
  }, [bookId, onRefresh]);
}